/**
 * Display formatters shared by the ops and customer portals. Currency and
 * timestamp rendering must look identical in both — keep the single source
 * of truth here.
 */

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

const usdCompactFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

export function formatUsd(n: number | null | undefined, opts?: { compact?: boolean }): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return (opts?.compact ? usdCompactFormatter : usdFormatter).format(n);
}

/** Short timestamp ("May 17, 11:14 PM"). */
export function formatTs(input: string | Date | null | undefined): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDate(
  input: string | Date | null | undefined,
  month: 'short' | 'long' = 'short',
): string {
  if (!input) return '—';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month,
    day: 'numeric',
  });
}

/**
 * Parse a yyyy-mm-dd calendar-date string as a LOCAL date (never UTC
 * midnight — `new Date('yyyy-mm-dd')` parses as UTC and shifts the displayed
 * day backwards west of Greenwich). A DATE column has no timezone of its own.
 */
export function parseISODate(iso: string): Date {
  return new Date(Number(iso.slice(0, 4)), Number(iso.slice(5, 7)) - 1, Number(iso.slice(8, 10)));
}

/**
 * 'Jul 4, 2026' from a yyyy-mm-dd calendar-date string — local-timezone
 * parse. SIL-322: consolidated from duplicate `formatDayMonthYear` (customer
 * lib/dates.ts) and `formatDateOnly` (ops listings.tsx) implementations;
 * used by the settlement-date rule in ListingStatusBadge.
 */
export function formatDayMonthYear(iso: string): string {
  return parseISODate(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Human byte size ("128 MB", "512 KB", "2.1 GB") — binary (1024) units,
 * one decimal place above KB. SIL-613: this is the ONLY place the VRAM
 * pattern-test panel's `{bytesTested}` copy is formatted, so every render
 * site (passport, listing, evidence-sources panel) shows the identical
 * string for the same byte count.
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex++;
  }
  const rounded = unitIndex === 0 ? Math.round(value) : +value.toFixed(1);
  return `${rounded} ${units[unitIndex]}`;
}
